// src/services/stripe.js
// Vanilla Stripe.js v3 (https://js.stripe.com/v3), loaded as an external
// script — same pattern as services/razorpay.js's loadRazorpay(), rather
// than adding @stripe/stripe-js / @stripe/react-stripe-js as new
// dependencies. Used for Guardian Dubai (AED) subscriber plan purchases;
// Razorpay Checkout (existing razorpay.js) is unchanged and still used for
// INR.
//
// 30 Jul 2026 — publishable key now comes from GET /api/public/stripe-config
// (PublicConfigController.java) instead of a build-time
// REACT_APP_STRIPE_PUBLISHABLE_KEY env var. Not a security change — the
// publishable key was never sensitive — but it means rotating the key or
// switching test/live mode is a Railway env var change only, no Vercel
// rebuild needed.
//
// Usage (SubscriberCheckoutPage.jsx):
//   const stripe = await getStripe();
//   const elements = stripe.elements({ clientSecret });
//   const paymentElement = elements.create('payment');
//   paymentElement.mount(containerRef.current);
//   ...
//   const { error, paymentIntent } = await stripe.confirmPayment({
//     elements, redirect: 'if_required',
//   });

const STRIPE_SCRIPT_URL = 'https://js.stripe.com/v3';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

let stripeJsPromise = null;
let publishableKeyPromise = null;

/** @returns {Promise<boolean>} whether window.Stripe is available after load. */
export function loadStripeScript() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Stripe) return Promise.resolve(true);
  if (stripeJsPromise) return stripeJsPromise;

  stripeJsPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = STRIPE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => { stripeJsPromise = null; resolve(false); };
    document.body.appendChild(script);
  });
  return stripeJsPromise;
}

/**
 * Fetches the publishable key from the backend, once per page load
 * (cached in the module-level promise — every checkout mount after the
 * first reuses it instead of re-fetching).
 */
function fetchPublishableKey() {
  if (publishableKeyPromise) return publishableKeyPromise;

  publishableKeyPromise = fetch(`${API_BASE}/public/stripe-config`)
    .then(res => {
      if (!res.ok) throw new Error(`stripe-config returned HTTP ${res.status}`);
      return res.json();
    })
    .then(data => {
      const key = (data?.publishableKey || '').trim();
      if (!key) throw new Error('Backend returned an empty Stripe publishable key.');
      return key;
    })
    .catch(err => {
      publishableKeyPromise = null; // don't cache a failure — allow retry on next call
      throw err;
    });

  return publishableKeyPromise;
}

/**
 * Loads Stripe.js and returns an initialised `stripe` instance, or throws if
 * the script failed to load or the backend's key fetch failed.
 */
export async function getStripe() {
  const [scriptOk, key] = await Promise.all([
    loadStripeScript(),
    fetchPublishableKey(),
  ]);
  if (!scriptOk) throw new Error('Failed to load Stripe.js.');
  return window.Stripe(key);
}
